import React, { useEffect, useState } from 'react';
import { DateRangePicker } from '@quintype/em/components/date-range-picker';
import { AnimatedSpinner } from '@quintype/em/icons/animated-spinner';
import { ToolTip } from '@quintype/em/components/tooltip';
import addDays from 'date-fns/addDays';
import set from 'date-fns/set';
import getUnixTime from 'date-fns/getUnixTime';
import fromUnixTime from 'date-fns/fromUnixTime';
import isAfter from 'date-fns/isAfter';
import isEqual from 'date-fns/isEqual';

import {
  downloadSubscriptionsToRenewCsv,
  getSubscriptionsDueToRenew
} from '../../api';
import Select from '../../components/library-components/SelectFieldWithLabel/SelectFieldWithLabel';
import { Button, Card } from '../../components/library-components';

import './subscriptions_due_renew.scss';

const intervalOptions = [
  { value: 1, label: 'Today' },
  { value: 7, label: 'Next 7 days' },
  { value: 15, label: 'Next 15 days' },
  { value: 30, label: 'Next 30 days' },
  { value: 60, label: 'Next 60 days' },
  { value: 90, label: 'Next 90 days' },
  { value: 'custom', label: 'Custom date range' }
];

const getUTCformattedDate = (date) => {
  const isoDate = date.toISOString();
  return isoDate.slice(0, 10); // formated date yyyy-mm-dd
};

const getDatesFromInterval = (interval) => {
  if (interval === 1) {
    return [new Date(), new Date()];
  }
  const startDate = addDays(new Date(), 1);
  const endDate = addDays(new Date(), interval);
  return [startDate, endDate];
};

const getInitialUnixStamp = () => {
  const tomorrow = addDays(new Date(), 1);
  const sevenDaysAfter = addDays(new Date(), 7);

  return [getUnixTime(tomorrow), getUnixTime(sevenDaysAfter)];
};

const getAPIdates = (interval, timestamp) => {
  if (typeof interval.value === 'string') {
    return {
      from_date: getUTCformattedDate(fromUnixTime(timestamp.start)),
      to_date: getUTCformattedDate(fromUnixTime(timestamp.end))
    };
  }

  const [fromDate, toDate] = getDatesFromInterval(interval.value);
  return {
    from_date: getUTCformattedDate(fromDate),
    to_date: getUTCformattedDate(toDate)
  };
};

// Reusable FutureSubscriptions component
export const FutureSubscriptions = ({
  title,
  cardTitle,
  tooltipId,
  subscriptionType,
  onDownloadClick
}) => {
  const [timestamp, setTimeStamp] = useState({
    start: getInitialUnixStamp()[0],
    end: getInitialUnixStamp()[1]
  });
  const [loading, setLoading] = useState(true);
  const [value, setValue] = useState(0);
  const [interval, setInterval] = useState(intervalOptions[0]);

  const getApiParams = () => ({
    ...getAPIdates(interval, timestamp),
    subscription_type: subscriptionType
  });

  const generateReport = () => {
    downloadSubscriptionsToRenewCsv(getApiParams()).then(
      (message) => {
        onDownloadClick(message, true);
      }
    );
  };

  useEffect(() => {
    const getData = () => {
      getSubscriptionsDueToRenew(getApiParams()).then(
        (result) => {
          setLoading(false);
          setValue(result);
        }
      );
    };
    setLoading(true);
    getData();
  }, [interval.value, timestamp.start, timestamp.end, subscriptionType]);

  const formattedValue = new Intl.NumberFormat('en-US', {
    notation: 'compact',
    compactDisplay: 'short'
  }).format(value);

  return (
    <div className="future-renewals__container">
      <div className="future-renewals__header">
        <h2 className="future-renewals__title">{title}</h2>
        <div className="future-renewals__filters">
          <div className="future-renewals__interval">
            <Select
              options={intervalOptions}
              onChange={setInterval}
              value={interval}
              label="Interval"
              clearable={false}
            />
            {interval.value === 'custom' && (
              <DateRangePicker
                startTimeStamp={timestamp.start}
                endTimeStamp={timestamp.end}
                onDateRangeSelect={(start, end) => {
                  setTimeStamp({
                    start,
                    end
                  });
                }}
                customValidation={({ startTimeStamp }) => {
                  const startDate = fromUnixTime(startTimeStamp);
                  const today = set(new Date(), {
                    hours: 0,
                    minutes: 0,
                    seconds: 0,
                    milliseconds: 0
                  });
                  const isTodayOrFutureDate =
                    isAfter(startDate, today) || isEqual(startDate, today);
                  if (!isTodayOrFutureDate) {
                    return {
                      type: 'from',
                      message: 'From date should be in future'
                    };
                  }
                }}
              />
            )}
          </div>
        </div>
      </div>
      <article className="sdr__container">
        <Card>
          <h3 className="sdr__title">{cardTitle}</h3>
          <div className="sdr__content-container">
            <div className="sdr__count">
              {loading
                ? <AnimatedSpinner width="32" height="32" />
                : (
                  <span data-for={tooltipId} data-tip={true}>
                    {formattedValue}
                    <ToolTip
                      value={`${value}`}
                      id={tooltipId}
                      place="top"
                      classname="sdr__tooltip"
                    />
                  </span>
                )
              }
            </div>
            <div className="sdr__download_container">
              <Button onClick={generateReport} primary>
                Download List
              </Button>
            </div>
          </div>
        </Card>
      </article>
    </div>
  );
};

const SubscribersdueToRenew = ({ onDownloadClick }) => (
  <FutureSubscriptions
    title="FUTURE RECURRING RENEWALS"
    cardTitle="Due To Renew"
    tooltipId="sdr-tooltip"
    subscriptionType="Recurring"
    onDownloadClick={onDownloadClick}
  />
);

export default SubscribersdueToRenew;

export const FutureOneTimeExpiry = ({ onDownloadClick }) => (
  <FutureSubscriptions
    title="FUTURE ONE TIME EXPIRY"
    cardTitle="Due To Expire"
    tooltipId="ote-tooltip"
    subscriptionType="One Time"
    onDownloadClick={onDownloadClick}
  />
);
